import {
  Box, Button, Chip, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography,
} from '@mui/material';
import { agruparPorTemporada, etiquetaEpisodio, nombreTemporada } from '@film/shared';
import type { EstadoDescarga, FicheroRelease, ReleaseVista } from '../lib/api.js';
import { describirEstado } from '../lib/estados.js';
import { formatearTamano } from '../lib/eventos.js';

/** Qué se está encolando ahora mismo, para apagar solo ese botón y no la tabla entera. */
export type EncolandoSerie =
  | { tipo: 'episodio'; fileId: string }
  | { tipo: 'temporada'; elinkId: number; season: number | null }
  | null;

interface Props {
  version: ReleaseVista;
  /** El cruce con Plex fue por título, no por IMDb: los «En Plex» se marcan, pero se dice. */
  cruceInseguro?: boolean;
  encolando: EncolandoSerie;
  onEpisodio: (fileId: string) => void;
  onTemporada: (season: number | null) => void;
}

/** Los ficheros de una versión de una serie, por temporada (D-27).
 *
 *  Es la misma agrupación que usa la API para encolar «la temporada N»: lo que se ve aquí es
 *  exactamente lo que se pide. Los vídeos a los que el sitio no puso temporada van al final,
 *  como «Sin numerar», y se avisa de que se colocarán a mano: no se les inventa una. */
export function TemporadasDeVersion({ version, cruceInseguro = false, encolando, onEpisodio, onTemporada }: Props) {
  const temporadas = agruparPorTemporada(version.files);
  if (!temporadas.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Este grupo no trae ningún vídeo.
      </Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 2, bgcolor: 'action.hover' }}>
      {temporadas.map((t) => {
        const pedidos = t.ficheros.filter((f) => f.descarga).length;
        // Lo que ya está en Plex tampoco hay que pedirlo: «descargar temporada» es «los
        // que faltan», y el servidor los salta igual (D-27).
        const enPlex = t.ficheros.filter((f) => f.enPlex && !f.descarga).length;
        const faltan = t.ficheros.length - pedidos - enPlex;
        const todosPedidos = faltan === 0;
        const encolandoEsta =
          encolando?.tipo === 'temporada' && encolando.elinkId === version.elinkId && encolando.season === t.season;
        return (
          <Box key={t.season ?? 'sin'}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 0.5 }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">{nombreTemporada(t.season)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t.episodios} {t.episodios === 1 ? 'episodio' : 'episodios'}
                  {t.sizeBytes != null && ` · ${formatearTamano(t.sizeBytes)}`}
                  {enPlex > 0 && ` · ${enPlex} en Plex`}
                  {pedidos > 0 && ` · ${pedidos} en descargas`}
                </Typography>
                {t.season == null && (
                  <Tooltip title="El sitio no dice de qué temporada son estos ficheros. Se pueden bajar igual, pero habrá que colocarlos a mano.">
                    <Chip size="small" color="warning" variant="outlined" label="colocar a mano" />
                  </Tooltip>
                )}
              </Stack>
              <Button
                size="small"
                variant={todosPedidos ? 'outlined' : 'contained'}
                disabled={todosPedidos || encolandoEsta}
                onClick={() => onTemporada(t.season)}
              >
                {todosPedidos
                  ? enPlex > 0 && pedidos === 0 ? 'Ya la tienes' : 'Ya en descargas'
                  : encolandoEsta
                    ? 'Encolando…'
                    : faltan < t.episodios
                      ? `Descargar los ${faltan} que faltan`
                      : 'Descargar temporada'}
              </Button>
            </Stack>

            <Table size="small" sx={{ '& td': { borderBottom: 'none', py: 0.25 } }}>
              <TableBody>
                {t.ficheros.map((f) => (
                  <FilaEpisodio
                    key={f.position}
                    fichero={f}
                    cruceInseguro={cruceInseguro}
                    encolando={encolando?.tipo === 'episodio' && encolando.fileId === f.id}
                    onEncolar={() => f.id && onEpisodio(f.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        );
      })}
    </Stack>
  );
}

function FilaEpisodio({
  fichero: f, cruceInseguro, encolando, onEncolar,
}: { fichero: FicheroRelease; cruceInseguro: boolean; encolando: boolean; onEncolar: () => void }) {
  const etiqueta = etiquetaEpisodio(f.season, f.episode, f.episodeEnd);
  return (
    <TableRow>
      <TableCell sx={{ width: 64, whiteSpace: 'nowrap' }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{etiqueta ?? '—'}</Typography>
      </TableCell>
      <TableCell sx={{ maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <Typography variant="caption" title={f.fileName}>{f.fileName}</Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="caption" color="text.secondary">{formatearTamano(f.sizeBytes)}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {f.descarga ? (
          <EstadoEpisodio status={f.descarga.status} />
        ) : f.enPlex ? (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip
              title={
                cruceInseguro
                  ? 'Este episodio está en Plex en una serie del mismo título, cruzada por nombre y no por IMDb. Se puede volver a bajar igualmente.'
                  : 'Este episodio ya está en tu biblioteca de Plex. Se puede volver a bajar, por ejemplo en mejor calidad.'
              }
            >
              <Chip size="small" color="success" variant="outlined" label={cruceInseguro ? 'En Plex (¿?)' : 'En Plex'} />
            </Tooltip>
            <Button size="small" variant="text" color="inherit" disabled={!f.id || encolando} onClick={onEncolar}>
              {encolando ? 'Encolando…' : 'Bajar igual'}
            </Button>
          </Stack>
        ) : (
          <Button size="small" variant="text" disabled={!f.id || encolando} onClick={onEncolar}>
            {encolando ? 'Encolando…' : 'Descargar'}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/** La ficha no tiene las fuentes de la descarga, solo su estado: los matices de
 *  «Esperando turno» o «Buscando fuentes» son de Descargas. Aquí basta decir en qué punto
 *  está, con las mismas palabras. */
function EstadoEpisodio({ status }: { status: EstadoDescarga }) {
  const e = describirEstado({ status, sourcesTotal: 1, sourcesXfer: 1, errorCode: null });
  return <Chip size="small" color={e.color} variant="outlined" label={e.texto} />;
}
